/* eslint-disable react-hooks/exhaustive-deps */
import { createStyles, IconButton, makeStyles, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { ChartFilter } from '../ChartComponents';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, formatNumber, GetGroupBy, getLabels, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import { DrillDownType } from '../DrillDownType';
import { SelectedItemProps } from '../MattersByStatusChart';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { useScreenshot, createFileName } from 'use-react-screenshot';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import MTooltip from '@material-ui/core/Tooltip';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartEmptyState } from '../ChartEmptyState';
import AccountBalanceWalletOutlinedIcon from '@material-ui/icons/AccountBalanceWalletOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px 0px 15px',
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            '& .titleWrapper': {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                '& .title': {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .value': {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .secondary-title': {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            position: 'relative'
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        total: {
            fontSize: '2.5rem',
            padding: '10px 0',
            fontWeight: 600,
            cursor: 'pointer',
        },
        span: {
            fontSize: '1rem',
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        scrollUp: {
            position: 'absolute',
            top: '30px',
            justifyContent: 'center',
            transform: 'translateX(-50%)',
            left: '50%',
            minWidth: '54px',
        },
        scrollDown: {
            position: 'absolute',
            bottom: '90px',
            justifyContent: 'center',
            transform: 'translateX(-50%)',
            left: '50%',
            minWidth: '54px',
        }
    }),
);

interface DebtorDaysChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
    groupBy: string;
}

interface ChartState {
    groupedBy: string[];
    matters: number[];
    amount: number[];
    debtorDaysAmount: number[];
    total: number;
    groupedByCount: number;
    symbol: string;
}

export const DebtorDaysChart: React.FC<DebtorDaysChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const entireChartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const scrollUpAndPrevRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const scrollDownAndNextRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const { loading, error, data } = useQuery<ExportDebtorsData>(DEBTORS_DAYS_QUERY, {
        variables: {
            groupBy: props.groupBy,
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
            showAllRecords: props.showAllRecords
        },
        fetchPolicy: 'network-only',
    });

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);

    const [state, setState] = useState<ChartState>({
        groupedByCount: 0,
        total: 0,
        groupedBy: [],
        matters: [],
        amount: [],
        debtorDaysAmount: [],
        symbol: '$',
    });

    const takeScreenShot = useScreenshot({
        type: 'image/jpeg',
        quality: 1.0
    })[1];

    useEffect(() => {
        if (!loading) {
            setState((prevState) => {
                let total = 0;
                let groupedByCount = 0;
                let groupedBy: string[] = [];
                let matters: number[] = [];
                let amount: number[] = [];
                let debtorDaysAmount: number[] = [];

                if (data && data.statistics && !loading && !error) {
                    total = data.statistics.matter.debtorsDays.total;
                    groupedByCount = data.statistics.matter.debtorsDays.groupedByCount;
                    groupedBy = data.statistics.matter.debtorsDays.debtorDays.map((s) => s.groupedBy);
                    matters = data.statistics.matter.debtorsDays.debtorDays.map((s) => s.matters);
                    amount = data.statistics.matter.debtorsDays.debtorDays.map((s) => s.amount);
                    debtorDaysAmount = data.statistics.matter.debtorsDays.debtorDays.map((s) => s.debtorDaysAmount);
                }

                return {
                    ...prevState,
                    total,
                    groupedByCount,
                    groupedBy,
                    matters,
                    amount,
                    debtorDaysAmount,
                };
            });
        }
        // tslint:disable-next-line: align
    }, [loading, error, data]);

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setState((prevState) => {
                return {
                    ...prevState,
                    symbol: currencySymbolQuery.data!.options.currencySymbol.symbol,
                };
            });
        }

        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart('DebtorDaysChartOpen');
        }
    };

    const handleTotalClick = () => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            searchCriterias.push({ property: 'DebtOwing', operator: 'gt', comparison: '0' });

            props.getSelectedItem(searchCriterias, true, `Debtor Days by ${GetGroupBy(props.groupBy)}`, DrillDownType.matterSummaryDrilldown, 'Debtors');
        }
    };

    const onMaximize = () => {
        if (props.onMaximize) {
            props.onMaximize();
        }
    };

    const onScrollUpAndPrev = (type: 'up' | 'prev') => {
        if (chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min > 0) {
            const scrollRatio = chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max - chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min;
            const min = chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min;
            if (scrollRatio < chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min) {
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min -= scrollRatio ? scrollRatio : 1;
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max -= scrollRatio ? scrollRatio : 1; 
            } else {
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min -= min;
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max -= min;
            }
            chartRef.current.options.scales[type === 'up' ? 'x' : 'y'].min = 0;
            chartRef.current.update();
            // enabled scroll up
            scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
        } else {
            // disabled scroll up
            scrollUpAndPrevRef.current.classList.add('Mui-disabled');
        }
        scrollDownAndNextRef.current.classList.remove('Mui-disabled');
    };
    
    const onScrollDownAndNext = (type: 'down' | 'next') => {

        const labels = state.groupedBy;

        if (chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max < labels.length) {
            const remainingBar = labels.length - chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max;
            const scrollRatio = chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max - chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min;
            if (remainingBar <= scrollRatio) {
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min += remainingBar;
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max += remainingBar;
            } else {
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min += scrollRatio ? scrollRatio : 1;
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max += scrollRatio ? scrollRatio : 1;
            }
            chartRef.current.options.scales[type === 'down' ? 'x' : 'y'].min = 0;
            chartRef.current.update();
            // enabled scroll down
            scrollDownAndNextRef.current.classList.remove('Mui-disabled');
        } else {
            // disabled scroll down
            scrollDownAndNextRef.current.classList.add('Mui-disabled');
        }
        scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
    };

    const onScollBar = (type: 'up' | 'down' | 'prev' | 'next') => () => {
        if (type === 'up') {
            onScrollUpAndPrev('up');
        } else if (type === 'down') {
            onScrollDownAndNext('down');
        }  else if (type === 'next') {
            onScrollDownAndNext('next');
        } else {
            onScrollUpAndPrev('prev');
        } 
    };

    const onDisabledEnabledArrow = (barType: 'horizontal' | 'vertical') => {  
        const labels = state.groupedBy;
        // up 
        if (chartRef.current.options.scales[barType === 'horizontal' ? 'y' : 'x'].min > 0) {
            scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
        } else {
            scrollUpAndPrevRef.current.classList.add('Mui-disabled');
        }

        // down
        if (chartRef.current.options.scales[barType === 'horizontal' ? 'y' : 'x'].max < labels.length) {
            scrollDownAndNextRef.current.classList.remove('Mui-disabled');
        } else {
            scrollDownAndNextRef.current.classList.add('Mui-disabled');
        }
    };

    const onPan = (context: { chart: Chart }) => {
        onDisabledEnabledArrow('horizontal');

        chartRef.current.options.scales.x.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };
    
    const onZoom = (context: { chart: Chart }) => {
        onDisabledEnabledArrow('horizontal');

        chartRef.current.options.scales.x.min = 0;
        chartRef.current.update();
    };

    // tslint:disable-next-line: no-any
    const onContextMenu = (event: any) => {
        event.preventDefault();
        if (event.button !== 2) {
            return false;
        }
        return true;
    };

    // tslint:disable-next-line: no-any
    const onDownload = (name: string, extension: string = 'jpg') => (image: any) => {
        const a = document.createElement('a');
        a.href = image;
        a.download = createFileName(extension, name);
        a.click();
    };

    const onDownloadScreenshot = () => {
        setTimeout(() => {
            takeScreenShot(entireChartRef.current).then(onDownload('Debtor Days'));
        // tslint:disable-next-line: align
        }, 500);
    };

    const chartControllerComponent = () => {
        return (
            <>
                <IconButtonWrapper aria-label="close">
                    <ChartInfo 
                        description={props.description}
                        fontSize={props.showAllRecords ? 'large' : 'medium'}
                        className={`${classes.helpIcon} rv-chart-help`}
                    />
                    {props.showAllRecords && (
                        <IconButton size="medium" onClick={onDownloadScreenshot}>
                            <GetAppOutlinedIcon color="primary" fontSize="large" />
                        </IconButton>
                    )}
                    {!props.showAllRecords && props.onMaximize && (
                        <MTooltip title="Expand chart">
                            <IconButton aria-label="maximize" onClick={onMaximize} size="small">
                                <AspectRatioIcon />
                            </IconButton>
                        </MTooltip>
                    )}
                    <IconButton aria-label="close" onClick={onCloseChart} size={props.showAllRecords ? 'medium' : 'small'}>
                        <CloseIcon className={classes.closeIcon} fontSize={props.showAllRecords ? 'large' : 'medium'} />
                    </IconButton>
                </IconButtonWrapper>
            </>
        );
    };
    
    if (loading || currencySymbolQuery.loading) {
        return (
            <div className="rv-chart rv-chart-loading">
                {chartControllerComponent()}
                <ChartSkeleton variant="bars" />
            </div>
        );
    }

    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.debtorDaysAmount) ? ' rv-chart--empty' : ''}`} ref={entireChartRef}>
            {chartControllerComponent()}
            <div className={classes.titleContainer}>
                <ChartHeading icon={<AccountBalanceWalletOutlinedIcon />} title="Debtor Days" />
                <div className={`${classes.total} h2`} onClick={() => handleTotalClick}>
                    <AnimatedNumber value={state.total} format={(amount) => formatNumber(amount)} />
                </div>
                <div className={classes.title}>By {GetGroupBy(props.groupBy)}</div>
            </div>
            <div className={classes.barContainer} style={{ maxHeight: props.showAllRecords ? '100%' : '350px' }}>
                {props.showAllRecords && (
                    <>
                        <div className={`${classes.scrollUp} scroll`}>
                            <IconButton 
                                size="medium" 
                                onClick={onScollBar('up')}
                                ref={scrollUpAndPrevRef}
                            >
                                <KeyboardArrowUpIcon color="primary" fontSize="large" />
                            </IconButton>
                        </div>
                        <div className={`${classes.scrollDown} scroll`}>
                            <IconButton 
                                size="medium" 
                                onClick={onScollBar('down')}
                                ref={scrollDownAndNextRef}
                            >
                                <KeyboardArrowDownIcon color="primary" fontSize="large" />
                            </IconButton>
                        </div>
                    </>
                )}
                <Bar
                    ref={chartRef}
                    onContextMenu={onContextMenu}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            const _index = getElementAtEvent(chartRef.current, event)[0].index;

                            const item = state.groupedBy[_index];

                            const searchCriterias = new Array<SearchCriteria>();
                            searchCriterias.push(
                                {property: props.groupBy, operator: 'eq', comparison: item }, 
                                { property: 'DebtOwing', operator: 'gt', comparison: '0' }
                            );

                            props.getSelectedItem(
                                searchCriterias, 
                                true, 
                                `Debtor Days by ${GetGroupBy(props.groupBy)}`, 
                                DrillDownType.matterSummaryDrilldown, 
                                'Debtors'
                            );
                        }
                    }}
                    data={{
                        labels: getLabels(state.groupedBy),
                        datasets: [
                            {
                                label: 'Days',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary, true),
                                data: state.debtorDaysAmount,
                            },
                        ],
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'y' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            x: {
                                grid: rvValueAxisStyle.grid,
                                // beginAtZero: true,
                                stacked: true,
                                suggestedMax: getSuggestedMaxTick(state.debtorDaysAmount),
                                beginAtZero: true,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit(state.debtorDaysAmount),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${formatNumber(tickValue)}`;
                                    },
                                },
                            },
                            y: {
                                grid: rvCategoryAxisStyle.grid,
                                min: 0,
                                max: props.showAllRecords ? 29 : 9,
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false,
                                },
                                // min: 0,
                                // max: 9 // minimum display
                            }
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: false,
                            },
                            title: {
                                display: false,
                                text: ``,
                            },
                            zoom: {
                                pan: {
                                    enabled: props.showAllRecords,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: props.showAllRecords,
                                    },
                                    onZoom: onZoom,
                                },
                                // limits: {
                                //   y: { min: 0, max: 9 },
                                // },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            //   dataIndex,
                                            //   label,
                                            formattedValue,
                                            dataset,
                                        } = context;

                                        var currentValue = convertStringNumberToNumber(formattedValue);

                                        return `${dataset.label}: ${formatNumber(currentValue)}`;
                                    },
                                    // tslint:disable-next-line: no-any no-shadowed-variable
                                    afterLabel: function (context: any) {

                                        const {
                                              dataIndex,
                                            //   label,
                                            // formattedValue,
                                            // dataset,
                                        } = context;

                                        const amount = state.amount[dataIndex];
                                        const matter = state.matters[dataIndex];

                                        return [
                                            `Total Debt: ${state.symbol}${formatNumber(amount)}`,
                                            `Matters: ${formatNumber(matter)}`,
                                        ];
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.debtorDaysAmount) && (
                    <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                )}
            </div>
            {/* <span className={classes.span}>Information collated based on Timekeeper</span>
            <span className={classes.span}>Drill down to timekeeper details</span>     
            <span className={classes.span}>The top 10 timekeepers are shown</span>                           */}
        </div>
    );
};

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

export interface ExportDebtorsData {
    statistics: Statistics;
}

export interface Statistics {
    matter: Matter;
}

export interface Matter {
    debtorsDays: DebtorsDays;
}

export interface DebtorsDays {
    total: number;
    groupedByCount: number;
    debtorDays: DebtorDay[];
}

export interface DebtorDay {
    groupedBy: string;
    matters: number;
    amount: number;
    debtorDaysAmount: number;
}

export const DEBTORS_DAYS_QUERY = gql`
    query DebtorDays($groupBy: MatterGroupBy, $entityType: Int, $entityGuid: String, $responsibility: Int, $showAllRecords: Boolean) {
        statistics {
            matter {
                debtorsDays(groupBy: $groupBy, entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility, showAllRecords: $showAllRecords) {
                    total
                    groupedByCount
                    debtorDays {
                        groupedBy
                        matters
                        amount
                        debtorDaysAmount
                    }
                }
            }
        }
    }
`;
